import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function FAQPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/faqs")).data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const filtered = items.filter((f) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q) || (f.tags || []).some((t) => t.toLowerCase().includes(q));
    });
    return filtered.reduce((acc, f) => {
      const k = f.category || "Allgemein";
      acc[k] = acc[k] || [];
      acc[k].push(f);
      return acc;
    }, {});
  }, [items, query]);

  const remove = async (id) => {
    if (!window.confirm("Eintrag löschen?")) return;
    try { await api.delete(`/faqs/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="crm-faq">
      <div className="mb-5 md:mb-8 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">Wissensbasis</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">Interne FAQs für schnelle Antworten und Onboarding.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B5D]" />
            <Input className="pl-9 w-56" placeholder="Durchsuchen…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="faq-search-input" />
          </div>
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={() => setCreating(true)} data-testid="faq-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Neu
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-6">
        {loading && <div className="text-sm text-[#5C6B5D]">Lädt…</div>}
        {!loading && Object.keys(groups).length === 0 && (
          <div className="text-sm text-[#5C6B5D] text-center py-8">Keine Einträge.</div>
        )}
        {Object.entries(groups).map(([cat, list]) => (
          <div key={cat} className="mb-6 last:mb-0">
            <div className="text-xs font-medium uppercase tracking-wider text-[#6B7F3E] mb-2">{cat}</div>
            <Accordion type="single" collapsible className="w-full">
              {list.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="border-[#E5E7EB]">
                  <AccordionTrigger className="text-sm font-medium hover:no-underline" data-testid={`faq-toggle-${f.id}`}>
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#4A5D4E] whitespace-pre-wrap">
                    {f.answer}
                    {f.tags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {f.tags.map((t) => (
                          <span key={t} className="text-[10px] font-medium bg-[#F5F6F0] text-[#2C3E2D] px-2 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => remove(f.id)}
                      data-testid={`faq-delete-${f.id}`}
                      className="mt-3 inline-flex items-center gap-1 text-xs text-[#B85C38] hover:text-[#7A2E14]"
                    >
                      <Trash2 className="w-3 h-3" /> Eintrag löschen
                    </button>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>

      <FAQDialog open={creating} onOpenChange={setCreating} onSaved={load} />
    </div>
  );
}

function FAQDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ question: "", answer: "", category: "Allgemein", tags: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ question: "", answer: "", category: "Allgemein", tags: "" }); }, [open]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.question || !form.answer) { toast.error("Frage und Antwort erforderlich"); return; }
    setSaving(true);
    try {
      await api.post("/faqs", {
        question: form.question, answer: form.answer, category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Gespeichert");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white">
        <DialogHeader><DialogTitle className="font-display">Neuer FAQ-Eintrag</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Frage *</Label><Input className="mt-1.5" value={form.question} onChange={(e) => update("question", e.target.value)} data-testid="faq-question-input" /></div>
          <div><Label>Antwort *</Label><Textarea rows={5} className="mt-1.5" value={form.answer} onChange={(e) => update("answer", e.target.value)} data-testid="faq-answer-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategorie</Label><Input className="mt-1.5" value={form.category} onChange={(e) => update("category", e.target.value)} data-testid="faq-category-input" /></div>
            <div><Label>Tags (kommasepariert)</Label><Input className="mt-1.5" value={form.tags} onChange={(e) => update("tags", e.target.value)} data-testid="faq-tags-input" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={save} disabled={saving} data-testid="faq-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
